import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  Link,
  Snackbar,
  Tooltip,
  Pagination,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Folder as FolderIcon,
  CreateNewFolder as CreateNewFolderIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import type { MediaFolder, MediaFile, MediaImage, MediaPagination } from '../../models/media';
import { 
  createFolder, 
  deleteFolder, 
  updateFolder, 
  getFolderContents,
  deleteFile,
  bulkDeleteFiles,
  // getFolderPath,
} from '../../services/media';

interface FolderBrowserProps {
  onSelectFile?: (file: MediaFile | MediaImage) => void;
  onUploadClick?: () => void;
  selectionMode?: boolean;
  fileTypeFilter?: 'all' | 'images' | 'documents';
  currentFolderId?: number | null | undefined;
  onFolderChange?: (folderId: number | null | undefined) => void;
  searchQuery?: string;
}

const FolderBrowser: React.FC<FolderBrowserProps> = ({ 
  onSelectFile,
  onUploadClick,
  selectionMode = false,
  fileTypeFilter = 'all',
  currentFolderId,
  onFolderChange,
  searchQuery = '',
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [images, setImages] = useState<MediaImage[]>([]);
  const [currentFolder, setCurrentFolder] = useState<MediaFolder | null>(null);
  const [folderPath, setFolderPath] = useState<MediaFolder[]>([]);

  // Pagination state: max 50 recent created show first
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [pagination, setPagination] = useState<MediaPagination | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(searchQuery);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce search query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  
  const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);
  const [renameItemType, setRenameItemType] = useState<'folder' | 'file'>('folder');
  const [renameItemId, setRenameItemId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [deleteType, setDeleteType] = useState<'folder' | 'file' | 'bulk'>('folder');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<(MediaFile | MediaImage)[]>([]);
  
  const [copySnackbarOpen, setCopySnackbarOpen] = useState<boolean>(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState<string>('');
  
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    type: 'folder' | 'file';
    item: MediaFolder | MediaFile | MediaImage;
  } | null>(null);
  
  // Load folder contents using callback
  const loadFolderContents = useCallback(async (
    folderId?: number | null | undefined,
    pageNum: number = 1,
    searchTerm: string = debouncedSearch
  ) => {
    setLoading(true);
    try {
      let typeParam: string | undefined = undefined;
      if (fileTypeFilter === 'images') typeParam = 'image';
      else if (fileTypeFilter === 'documents') typeParam = 'document';

      const contents = await getFolderContents(folderId, {
        page: pageNum,
        page_size: pageSize,
        type: typeParam,
        search: searchTerm.trim() || undefined,
      });

      setFolders(contents.folders || []);
      setFiles(contents.files || []);
      setImages(contents.images || []);
      setPagination(contents.pagination || null);
      
      // Update folder path
      if (folderId === undefined || folderId === null) {
        // Reset to root
        setCurrentFolder(null);
        setFolderPath([]);
        if (onFolderChange) {
          onFolderChange(null);
        }
      } else {
        // Set current folder from contents if available
        const folder = contents.folders.find(f => f.id === folderId);
        if (folder) {
          setCurrentFolder(folder);
          
          setFolderPath(prevPath => {
            const newPath = [...prevPath];
            const existingIndex = newPath.findIndex(f => f.id === folder.id);
            if (existingIndex >= 0) {
              newPath.splice(existingIndex + 1);
            } else {
              newPath.push(folder);
            }
            return newPath;
          });
          
          if (onFolderChange) {
            onFolderChange(folder.id);
          }
        }
      }
    } catch (error) {
      console.error('Error loading folder contents:', error);
    } finally {
      setLoading(false);
    }
  }, [onFolderChange, fileTypeFilter, pageSize, debouncedSearch]);
  
  // Reset to page 1 and load when folder, filter, or search changes
  useEffect(() => {
    setPage(1);
    loadFolderContents(currentFolderId, 1, debouncedSearch);
  }, [currentFolderId, fileTypeFilter, debouncedSearch, loadFolderContents]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    loadFolderContents(currentFolderId, newPage, debouncedSearch);
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleOpenFolder = (folder: MediaFolder) => {
    setPage(1);
    if (onFolderChange) {
      onFolderChange(folder.id);
    } else {
      loadFolderContents(folder.id, 1, debouncedSearch);
    }
  };
  
  const handleNavigateUp = () => {
    setPage(1);
    if (folderPath.length === 0) {
      return;
    }
    
    if (folderPath.length === 1) {
      if (onFolderChange) {
        onFolderChange(null);
      } else {
        loadFolderContents(null, 1, debouncedSearch);
      }
    } else {
      const parentFolder = folderPath[folderPath.length - 2];
      if (onFolderChange) {
        onFolderChange(parentFolder.id);
      } else {
        loadFolderContents(parentFolder.id, 1, debouncedSearch);
      }
    }
  };
  
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setLoading(true);
    try {
      const parentId = currentFolder?.id;
      const newFolder = await createFolder(newFolderName, parentId);
      setFolders([...folders, newFolder]);
      setNewFolderName('');
      setCreateFolderDialogOpen(false);
    } catch (error) {
      console.error('Error creating folder:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteItem = async () => {
    setLoading(true);
    try {
      if (deleteType === 'folder' && deleteId) {
        await deleteFolder(deleteId);
        setFolders(folders.filter(f => f.id !== deleteId));
      } else if (deleteType === 'file' && deleteId) {
        await deleteFile(deleteId);
        setFiles(files.filter(f => f.id !== deleteId));
        setImages(images.filter(i => i.id !== deleteId));
      } else if (deleteType === 'bulk' && selectedFiles.length > 0) {
        const fileIds = selectedFiles.map(file => file.id);
        await bulkDeleteFiles(fileIds);
        setFiles(files.filter(f => !fileIds.includes(f.id)));
        setImages(images.filter(i => !fileIds.includes(i.id)));
        setSelectedFiles([]);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteId(null);
      setLoading(false);
    }
  };
  
  const handleRenameItem = async () => {
    if (!renameValue.trim() || renameItemId === null) return;
    
    setLoading(true);
    try {
      if (renameItemType === 'folder') {
        const updatedFolder = await updateFolder(renameItemId, renameValue);
        setFolders(folders.map(f => f.id === renameItemId ? updatedFolder : f));
      } else {
        // File renaming handled in parent component
      }
    } catch (error) {
      console.error('Error renaming item:', error);
    } finally {
      setRenameDialogOpen(false);
      setRenameItemId(null);
      setRenameValue('');
      setLoading(false);
    }
  };
  
  const handleContextMenu = (
    event: React.MouseEvent,
    type: 'folder' | 'file',
    item: MediaFolder | MediaFile | MediaImage
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      type,
      item,
    });
  };

  const handleCopyPath = async (item: MediaFile | MediaImage) => {
    // For images prefer the actual file URL; thumbnail is only a derivative
    const path = item.file;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(path);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = path;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopySnackbarMessage('File path copied to clipboard');
      setCopySnackbarOpen(true);
    } catch (error) {
      console.error('Failed to copy file path:', error);
      setCopySnackbarMessage('Failed to copy file path');
      setCopySnackbarOpen(true);
    }
  };
  
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };
  
  const handleFileClick = (file: MediaFile | MediaImage) => {
    if (selectionMode && onSelectFile) {
      onSelectFile(file);
    } else {
      // Toggle selection
      const isSelected = selectedFiles.some(f => f.id === file.id);
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(f => f.id !== file.id));
      } else {
        setSelectedFiles([...selectedFiles, file]);
      }
    }
  };
  
  const openRenameDialog = (type: 'folder' | 'file', id: number, currentName: string) => {
    setRenameItemType(type);
    setRenameItemId(id);
    setRenameValue(currentName);
    setRenameDialogOpen(true);
    handleCloseContextMenu();
  };
  
  const openDeleteDialog = (type: 'folder' | 'file', id: number) => {
    setDeleteType(type);
    setDeleteId(id);
    setConfirmDeleteOpen(true);
    handleCloseContextMenu();
  };
  
  const renderBreadcrumbs = () => {
    return (
      <Breadcrumbs 
        aria-label="folder navigation breadcrumb"
        separator="/"
        sx={{ 
          p: 1, 
          borderRadius: 1,
          bgcolor: 'background.paper', 
          '& .MuiBreadcrumbs-ol': { 
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'action.hover',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'divider',
              borderRadius: 3,
            },
          },
          boxShadow: 1,
          overflowX: 'auto',
          maxWidth: '100%'
        }}
      >
        <Link 
          component="button"
          underline="hover" 
          color="inherit" 
          onClick={() => {
            setPage(1);
            if (onFolderChange) {
              onFolderChange(null);
            } else {
              loadFolderContents(null, 1, debouncedSearch);
            }
          }}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            fontWeight: 'medium',
            color: 'primary.main'
          }}
        >
          <FolderIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
          Root
        </Link>
        {folderPath.map((folder, index) => (
          <Link
            key={folder.id}
            component="button"
            underline="hover"
            color={index === folderPath.length - 1 ? "text.primary" : "inherit"}
            onClick={() => {
              setPage(1);
              if (onFolderChange) {
                onFolderChange(folder.id);
              } else {
                loadFolderContents(folder.id, 1, debouncedSearch);
              }
            }}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              maxWidth: { xs: '100px', sm: '150px', md: '200px' },
              ...(index === folderPath.length - 1 ? { 
                fontWeight: 'bold',
                color: 'text.primary'
              } : {}),
              '&:hover': {
                bgcolor: 'action.hover',
                borderRadius: 1,
                px: 0.5
              }
            }}
          >
            <FolderIcon sx={{ mr: 0.5, fontSize: '0.9rem', color: 'warning.main' }} />
            <Typography noWrap title={folder.name}>
              {folder.name}
            </Typography>
          </Link>
        ))}
      </Breadcrumbs>
    );
  };
  
  // Combine files and images in chronological order
  const displayItems = useMemo(() => {
    if (fileTypeFilter === 'images') return images;
    if (fileTypeFilter === 'documents') return files;
    return [...files, ...images].sort((a, b) => {
      const timeA = new Date(a.uploaded_at).getTime() || 0;
      const timeB = new Date(b.uploaded_at).getTime() || 0;
      return timeB - timeA;
    });
  }, [files, images, fileTypeFilter]);

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      {loading && <LinearProgress />}
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {folderPath.length > 0 && (
          <IconButton onClick={handleNavigateUp}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          {renderBreadcrumbs()}
          {pagination && pagination.total_count > 0 && (
            <Chip
              label={`${pagination.total_count} ${pagination.total_count === 1 ? 'item' : 'items'}`}
              size="small"
              variant="outlined"
              sx={{ ml: 1, height: 24, fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <Button 
          startIcon={<CreateNewFolderIcon />}
          onClick={() => setCreateFolderDialogOpen(true)}
          sx={{ mr: 1 }}
        >
          New Folder
        </Button>
        {onUploadClick && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={onUploadClick}
          >
            Upload Files
          </Button>
        )}
      </Box>
      
      {selectedFiles.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {selectedFiles.length} items selected
          </Typography>
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            onClick={() => {
              setDeleteType('bulk');
              setConfirmDeleteOpen(true);
            }}
          >
            Delete Selected
          </Button>
        </Box>
      )}
      
      <Grid container spacing={2}>
        {/* Folders (shown on page 1) */}
        {page === 1 && folders.map(folder => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`folder-${folder.id}`}>
            <Card 
              onClick={() => handleOpenFolder(folder)}
              onContextMenu={(e) => handleContextMenu(e, 'folder', folder)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <FolderIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="subtitle1" noWrap>{folder.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {folder.child_count} folders, {folder.file_count} files
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openRenameDialog('folder', folder.id, folder.name);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog('folder', folder.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
        
        {/* Files & Images */}
        {displayItems.map(item => {
          const isSelected = selectedFiles.some(f => f.id === item.id);
          const isImage = item.is_image || 'thumbnail_url' in item;

          if (isImage) {
            const image = item as MediaImage;
            const thumbnailUrl = image.thumbnail_url?.small || image.file || '#';
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`image-${image.id}`}>
                <Card 
                  onClick={() => handleFileClick(image)}
                  onContextMenu={(e) => handleContextMenu(e, 'file', image)}
                  sx={{ 
                    cursor: 'pointer',
                    border: isSelected ? '2px solid' : 'none',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ 
                    height: 140, 
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'background.default'
                  }}>
                    {thumbnailUrl !== '#' ? (
                      <img 
                        src={thumbnailUrl} 
                        alt={image.name}
                        loading="lazy"
                        style={{ 
                          maxHeight: '100%', 
                          maxWidth: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                    )}
                  </Box>
                  <CardContent sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="subtitle2" noWrap title={image.name}>
                      {image.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {image.file_size_display}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                    <Tooltip title="Copy image path">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyPath(image);
                        }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete image">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog('file', image.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          }

          const file = item as MediaFile;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`file-${file.id}`}>
              <Card 
                onClick={() => handleFileClick(file)}
                onContextMenu={(e) => handleContextMenu(e, 'file', file)}
                sx={{ 
                  cursor: 'pointer',
                  border: isSelected ? '2px solid' : 'none',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <DocumentIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1" noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {file.original_filename} ({file.file_size_display})
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title="Copy file path">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyPath(file);
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete file">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteDialog('file', file.id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      {/* Empty state */}
      {folders.length === 0 && displayItems.length === 0 && !loading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 6,
          opacity: 0.7
        }}>
          <FolderIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6">This folder is empty</Typography>
          <Typography variant="body2">
            Upload files or create folders to organize your media
          </Typography>
        </Box>
      )}

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 3,
            pt: 2,
            pb: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {Math.min((page - 1) * pageSize + 1, pagination.total_count)}–
            {Math.min(page * pageSize, pagination.total_count)} of {pagination.total_count} items
          </Typography>

          <Pagination
            count={pagination.total_pages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            disabled={loading}
            size="medium"
          />
        </Box>
      )}
      
      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.type === 'folder' && (
          <>
            <MenuItem onClick={() => {
              if (contextMenu?.item) {
                openRenameDialog('folder', (contextMenu.item as MediaFolder).id, (contextMenu.item as MediaFolder).name);
              }
            }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Rename
            </MenuItem>
            <MenuItem onClick={() => {
              if (contextMenu?.item) {
                openDeleteDialog('folder', (contextMenu.item as MediaFolder).id);
              }
            }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </>
        )}
        
        {contextMenu?.type === 'file' && (
          <>
            <MenuItem
              onClick={() => {
                if (contextMenu?.item) {
                  handleCopyPath(contextMenu.item as MediaFile | MediaImage);
                  handleCloseContextMenu();
                }
              }}
            >
              <CopyIcon fontSize="small" sx={{ mr: 1 }} /> Copy path
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (contextMenu?.item) {
                  openDeleteDialog('file', (contextMenu.item as MediaFile).id);
                }
              }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </>
        )}
      </Menu>
      
      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialogOpen} onClose={() => setCreateFolderDialogOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>
      
      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>
          Rename {renameItemType === 'folder' ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={renameItemType === 'folder' ? 'Folder Name' : 'File Name'}
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameItem} disabled={!renameValue.trim()}>Rename</Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteType === 'folder' && 'Are you sure you want to delete this folder and all its contents?'}
            {deleteType === 'file' && 'Are you sure you want to delete this file?'}
            {deleteType === 'bulk' && `Are you sure you want to delete ${selectedFiles.length} selected item(s)?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteItem} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2500}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setCopySnackbarOpen(false);
        }}
        message={copySnackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default FolderBrowser;
